import { Box, DialogContent, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  BarChart, Bar,
  LineChart, Line,
  PieChart, Pie,
  CartesianGrid, XAxis, YAxis, Tooltip, Legend,
} from 'recharts';
import { CDialog, CDialogTitle, CDialogActions, CButton } from '../components/common/Modal';
import DataTable from '../components/DataTable';

const COLORS = ['#5D87FF', '#FA896B', '#13DEB9', '#FFAE1F', '#7B53EF'];

function ChartPreview({ data, chart, x_column, y_column }) {
  if (!data || data.length === 0) {
    return <Typography variant="body2" color="text.secondary">No data available.</Typography>;
  }

  const keys = Object.keys(data[0]);
  const xCols = x_column
    ? (Array.isArray(x_column) ? x_column : [x_column])
    : null;

  let effectiveData, dataKey, valueKey;

  if (xCols && xCols.length > 1) {
    // Pivot: each selected X column becomes its own category row.
    // Column name → X label, column value → Y value.
    effectiveData = [];
    data.forEach(row => {
      xCols.forEach(col => {
        effectiveData.push({ __x: col, __y: row[col] });
      });
    });
    dataKey = '__x';
    valueKey = '__y';
  } else {
    effectiveData = data;
    dataKey = xCols
      ? xCols[0]
      : (keys.find(k => typeof data[0][k] === 'string') || keys[0]);
    valueKey = y_column || keys.find(k => typeof data[0][k] === 'number') || keys[1];
  }

  const seriesName = valueKey === '__y' ? 'Value' : valueKey;

  const width = 520;
  const height = 300;

  switch (chart) {
    case 'line':
      return (
        <LineChart data={effectiveData} width={width} height={height} margin={{ left: 10, right: 10 }}>
          <CartesianGrid vertical={false} strokeOpacity={0.3} />
          <XAxis dataKey={dataKey} tick={{ fontSize: 12 }} type="category" />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey={valueKey} name={seriesName} stroke={COLORS[0]} dot={false} />
        </LineChart>
      );
    case 'pie':
      return (
        <PieChart width={width} height={height}>
          <Pie data={effectiveData} dataKey={valueKey} nameKey={dataKey} cx="50%" cy="50%" outerRadius={120} fill={COLORS[0]} />
          <Tooltip />
          <Legend />
        </PieChart>
      );
    default:
      return (
        <BarChart data={effectiveData} width={width} height={height} margin={{ left: 20, right: 20, top: 10, bottom: 10 }}>
          <CartesianGrid vertical={false} strokeOpacity={0.3} />
          <XAxis dataKey={dataKey} tick={{ fontSize: 12 }} type="category" />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip />
          <Legend />
          <Bar dataKey={valueKey} name={seriesName} fill={COLORS[0]} />
        </BarChart>
      );
  }
}

export default function PreviewModal({ open, title, data, visualization, chart, x_column, y_column, handleClose }) {
  const showChart = visualization === 'chart' || visualization === 'ultra';
  const showTable = visualization === 'table' || visualization === 'ultra';

  return (
    <CDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <CDialogTitle>Preview — {title}</CDialogTitle>
      <IconButton
        onClick={handleClose}
        sx={{ position: 'absolute', right: 6, top: 6, color: (theme) => theme.palette.grey[800] }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <DialogContent>
        {showChart && data && data.length > 0 && (
          <Box sx={{ mb: showTable ? 3 : 0 }}>
            <ChartPreview data={data} chart={chart} x_column={x_column} y_column={y_column} />
          </Box>
        )}
        {showTable && data && data.length > 0 && (
          <DataTable data={data} />
        )}
        {(!data || data.length === 0) && (
          <Typography variant="body2" color="text.secondary">No data to preview.</Typography>
        )}
      </DialogContent>
      <CDialogActions>
        <CButton onClick={handleClose}>Close</CButton>
      </CDialogActions>
    </CDialog>
  );
}
